import { Dtos, formatToDollar } from "@inrev/common";
import { useFetchBondPreviews } from "@portal/api/previews";
import {
	TrendMetricCard,
	type TrendMetricCardProps,
} from "@portal/components/patterns/metric-card";
import { useAuthenticatedUser } from "@portal/providers/AuthenticatedUserProvider";
import { useMemo } from "react";
import type { EntityScope } from "../../../shared/scope";
import { computeBondMetrics } from "./computeBondMetrics";

const EMPTY_BOND_PREVIEWS: Dtos.SuretyBond.Preview.Get.Response[] = [];

type BondMetricsViewProps = {
	scope: EntityScope;
};

export const BondMetricsView = ({ scope }: BondMetricsViewProps) => {
	const { user } = useAuthenticatedUser();
	const { bondPreviews } = useFetchBondPreviews();

	const metrics = useMemo(
		() =>
			computeBondMetrics({
				bondPreviews: bondPreviews ?? EMPTY_BOND_PREVIEWS,
				scope,
				currentUserId: user.id,
			}),
		[bondPreviews, scope, user.id],
	);

	const cards: TrendMetricCardProps[] = [
		{
			testId: "metric-open-bonds",
			label: "Open",
			value: metrics.open,
			tone: "accent",
		},
		{
			testId: "metric-open-bids",
			label: "Open Bids",
			value: metrics.openBids,
			tone: "accent",
		},
		{
			testId: "metric-open-finals",
			label: "Open Finals",
			value: metrics.openFinals,
			tone: "accent",
		},
		{
			testId: "metric-average-gwp",
			label: "Average GWP",
			value: formatToDollar(metrics.averageGwp, { abbreviate: true }),
			tone: "confirm",
		},
		{
			testId: "metric-average-bond-amount",
			label: "Average Bond Amount",
			value: formatToDollar(metrics.averageBondAmount, { abbreviate: true }),
			tone: "confirm",
		},
	];

	return (
		<div className="grid max-w-8xl grid-cols-3 gap-4 lg:grid-cols-5">
			{cards.map((card) => (
				<TrendMetricCard key={card.testId} {...card} />
			))}
		</div>
	);
};
